// @flow
import React from 'react';
import { Helmet } from 'react-helmet';
import format from 'date-fns/format';
import './ShippingTrackingTemplate.scss';

type Props = {
  tracking: Array<any>,
  courierName: string,
  invoiceNo: string,
  receiverName: string,
  receiverAddr: string,
};

const ShippingTrackingTemplate = (props: Props) => {
  const { tracking, courierName, invoiceNo, receiverName, receiverAddr } = props;

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    const days = ['일', '월', '화', '수', '목', '금', '토'];
    const dayOfWeek = days[date.getDay()];

    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');

    return `${month}/${day} (${dayOfWeek}) ${hours}:${minutes}`;
  };

  const getStatusText = (level: string) => {
    switch (level) {
      case '1':
        return '배송 준비중';
      case '2':
        return '집화완료';
      case '3':
        return '배송중';
      case '4':
        return '지점도착';
      case '5':
        return '배송출발';
      case '6':
        return '배송완료';
      default:
        return '배송 준비중';
    }
  };

  const getCurrentStatus = () => {
    if (!tracking || tracking.length === 0) return '배송 준비중';
    return getStatusText(tracking[0].level);
  };

  const getProgressStep = () => {
    if (!tracking || tracking.length === 0) return 0;
    const { level } = tracking[0];
    if (level === '1') return 0;
    if (level === '2') return 1;
    if (level === '3' || level === '4' || level === '5') return 2;
    if (level === '6') return 3;
    return 0;
  };

  const progressStep = getProgressStep();

  return (
    <div className="ShippingTrackingTemplate">
      <Helmet>
        <title>배송조회 | 캐시바이</title>
      </Helmet>
      <div className="tracking-header">
        <h1 className="tracking-title">배송조회</h1>
      </div>

      <div className="tracking-content">
        <div className="tracking-status-card">
          <div className="status-badge">{getCurrentStatus()}</div>

          <div className="courier-info">
            <div className="courier-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path
                  d="M19 7H16V4C16 3.45 15.55 3 15 3H5C4.45 3 4 3.45 4 4V17H5C5 18.66 6.34 20 8 20C9.66 20 11 18.66 11 17H15C15 18.66 16.34 20 18 20C19.66 20 21 18.66 21 17H22V12L19 7ZM8 18.5C7.17 18.5 6.5 17.83 6.5 17C6.5 16.17 7.17 15.5 8 15.5C8.83 15.5 9.5 16.17 9.5 17C9.5 17.83 8.83 18.5 8 18.5ZM18 18.5C17.17 18.5 16.5 17.83 16.5 17C16.5 16.17 17.17 15.5 18 15.5C18.83 15.5 19.5 16.17 19.5 17C19.5 17.83 18.83 18.5 18 18.5ZM18 13H16V8H18.5L20.46 11.5L18 13Z"
                  fill="currentColor"
                />
              </svg>
            </div>
            <div className="courier-details">
              <div className="courier-label">택배사</div>
              <div className="courier-name">{courierName}</div>
            </div>
          </div>

          <div className="invoice-info">
            <div className="invoice-label">운송장 번호</div>
            <div className="invoice-number">{invoiceNo}</div>
            <button
              className="copy-button"
              onClick={() => {
                navigator.clipboard.writeText(invoiceNo);
                alert('운송장 번호가 복사되었습니다.');
              }}
            >
              복사
            </button>
          </div>

          <div className="progress-bar">
            <div className="progress-steps">
              <div className={`progress-step ${progressStep >= 0 ? 'active' : ''}`}>
                <div className="step-circle"></div>
                <div className="step-label">배송 준비중</div>
              </div>
              <div className={`progress-line ${progressStep >= 1 ? 'active' : ''}`}></div>
              <div className={`progress-step ${progressStep >= 1 ? 'active' : ''}`}>
                <div className="step-circle"></div>
                <div className="step-label">집화</div>
              </div>
              <div className={`progress-line ${progressStep >= 2 ? 'active' : ''}`}></div>
              <div className={`progress-step ${progressStep >= 2 ? 'active' : ''}`}>
                <div className="step-circle"></div>
                <div className="step-label">배송중</div>
              </div>
              <div className={`progress-line ${progressStep >= 3 ? 'active' : ''}`}></div>
              <div className={`progress-step ${progressStep >= 3 ? 'active' : ''}`}>
                <div className="step-circle"></div>
                <div className="step-label">배송완료</div>
              </div>
            </div>
          </div>
        </div>

        <div className="shipping-info-card">
          <h3 className="card-title">배송지정보</h3>
          <div className="info-row">
            <div className="info-label">주소</div>
            <div className="info-value">
              {receiverAddr || '서울특별시 서초구 동광로1길 52 (반포동)'}
            </div>
          </div>
          <div className="info-row">
            <div className="info-label">받는 사람</div>
            <div className="info-value">{receiverName || '권정빈'}</div>
          </div>
        </div>

        {tracking && tracking.length > 0 && (
          <div className="tracking-history-card">
            <h3 className="card-title">배송 준비중</h3>
            <div className="timeline">
              {tracking.map((item, index) => (
                <div key={index} className="timeline-item">
                  <div className="timeline-marker">
                    <div className={`timeline-dot ${index === 0 ? 'active' : ''}`}></div>
                    {index !== tracking.length - 1 && <div className="timeline-line"></div>}
                  </div>
                  <div className="timeline-content">
                    <div className="timeline-status">{item.where}</div>
                    <div className="timeline-meta">
                      <span className="timeline-date">
                        {formatDateTime(item.time_trans)} · {item.details}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ShippingTrackingTemplate;



// WEBPACK FOOTER //
// ./src/components/order/ShippingTrackingTemplate/ShippingTrackingTemplate.js